/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * The actions for Money Transfer wizard
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.2.0
 */

import { createAction } from '../../modules/app/createAction';
import { IWizardValidationError } from '../../modules/wizard/wizardTypes';
import { ILogoutStartSuccessful } from '../PageAuth/types/authTypes';
import {
    MONEY_TRANSFER_CHECK_RECIPIENTS_FINISH,
    MONEY_TRANSFER_CHECK_RECIPIENTS_START,
    MONEY_TRANSFER_CLEAR_SEARCH_VALUE,
    MONEY_TRANSFER_HANDLE_NEXT,
    MONEY_TRANSFER_LOAD_ACCOUNT_LIST,
    MONEY_TRANSFER_LOAD_ACCOUNT_LIST_FAILED,
    MONEY_TRANSFER_LOAD_ACCOUNT_LIST_SUCCESSFUL,
    MONEY_TRANSFER_SET_ACCOUNT,
    MONEY_TRANSFER_SET_AMOUNT,
    MONEY_TRANSFER_SET_CONFLICTS,
    MONEY_TRANSFER_SET_PARAMS,
    MONEY_TRANSFER_SET_PARAMS_ERROR_CODE,
    MONEY_TRANSFER_SET_SEARCH_VALUE,
    MONEY_TRANSFER_STORAGE_SAVE_STEP,
    MONEY_TRANSFER_VALIDATE_SET_STATUS,
    MONEY_TRANSFER_VALIDATE_START,
} from './moneyTransferConstants';
import {
    IMoneyTransferClearSearchValuePayload,
    IMoneyTransferLoadAccountListFailedPayload,
    IMoneyTransferLoadAccountListPayload,
    IMoneyTransferLoadAccountListSuccessfulPayload,
    IMoneyTransferSetAccountPayload,
    IMoneyTransferSetParamsErrorCodePayload,
    IMoneyTransferSetParamsPayload,
    IMoneyTransferSetSearchValuePayload,
    IMoneyTransferSetStepValidPayload,
    IMoneyTransferStorageSaveStepPayload,
    IMoneyTransferValidateStartPayload,
    TMoneyTransferCheckRecipientsFinishAction,
    TMoneyTransferCheckRecipientsStartAction,
    TMoneyTransferClearSearchValueAction,
    TMoneyTransferHandleNextAction,
    TMoneyTransferLoadAccountListAction,
    TMoneyTransferLoadAccountListFailedAction,
    TMoneyTransferLoadAccountListSuccessfulAction,
    TMoneyTransferSetAccountAction,
    TMoneyTransferSetAmountAction,
    TMoneyTransferSetConflictsAction,
    TMoneyTransferSetParamsAction,
    TMoneyTransferSetParamsErrorCodeAction,
    TMoneyTransferSetSearchValueAction,
    TMoneyTransferStorageSaveStepAction,
    TMoneyTransferValidateSetStatusAction,
    TMoneyTransferValidateStartAction,
} from './types/moneyTransferActionsTypes';

/*
* -------------------------------------------
*           SET SEARCH VALUE
* -------------------------------------------
*/

export const moneyTransferSetSearchValue =
    (payload: IMoneyTransferSetSearchValuePayload): TMoneyTransferSetSearchValueAction =>
        createAction(MONEY_TRANSFER_SET_SEARCH_VALUE, payload);

/*
* -------------------------------------------
*           CLEAR SEARCH VALUE
* -------------------------------------------
*/

export const moneyTransferClearSearchValue =
    (payload: IMoneyTransferClearSearchValuePayload): TMoneyTransferClearSearchValueAction =>
        createAction(MONEY_TRANSFER_CLEAR_SEARCH_VALUE, payload);

/*
* -------------------------------------------
*           SET ACCOUNT
* -------------------------------------------
*/

export const moneyTransferSetAccount =
    (payload: IMoneyTransferSetAccountPayload): TMoneyTransferSetAccountAction =>
        createAction(MONEY_TRANSFER_SET_ACCOUNT, payload);

/*
* -------------------------------------------
*           SET PARAMS
* -------------------------------------------
*/

export const moneyTransferSetParams =
    (payload: IMoneyTransferSetParamsPayload): TMoneyTransferSetParamsAction =>
        createAction(MONEY_TRANSFER_SET_PARAMS, payload);

/*
* -------------------------------------------
*           SET PARAMS ERROR CODE
* -------------------------------------------
*/

export const moneyTransferSetParamsErrorCode =
    (payload: IMoneyTransferSetParamsErrorCodePayload): TMoneyTransferSetParamsErrorCodeAction =>
        createAction(MONEY_TRANSFER_SET_PARAMS_ERROR_CODE, payload);

/*
* -------------------------------------------
*           LOAD ACCOUNT LIST
* -------------------------------------------
*/

export const moneyTransferLoadAccountList =
    (payload: IMoneyTransferLoadAccountListPayload): TMoneyTransferLoadAccountListAction =>
        createAction(MONEY_TRANSFER_LOAD_ACCOUNT_LIST, payload);

export const moneyTransferLoadAccountListSuccessful =
    (payload: IMoneyTransferLoadAccountListSuccessfulPayload): TMoneyTransferLoadAccountListSuccessfulAction =>
        createAction(MONEY_TRANSFER_LOAD_ACCOUNT_LIST_SUCCESSFUL, payload);

export const moneyTransferLoadAccountListFailed =
    (payload: IMoneyTransferLoadAccountListFailedPayload): TMoneyTransferLoadAccountListFailedAction =>
        createAction(MONEY_TRANSFER_LOAD_ACCOUNT_LIST_FAILED, payload);

/*
* -------------------------------------------
*           SET AMOUNT
* -------------------------------------------
*/

export const moneyTransferSetAmount = (payload: string): TMoneyTransferSetAmountAction =>
    createAction(MONEY_TRANSFER_SET_AMOUNT, payload);

/*
* -------------------------------------------
*           VALIDATE
* -------------------------------------------
*/

export const moneyTransferValidateStart =
    (payload?: IMoneyTransferValidateStartPayload): TMoneyTransferValidateStartAction =>
        createAction(MONEY_TRANSFER_VALIDATE_START, payload);

export const moneyTransferValidateSetStatus =
    (payload: IMoneyTransferSetStepValidPayload): TMoneyTransferValidateSetStatusAction =>
    createAction(MONEY_TRANSFER_VALIDATE_SET_STATUS, payload);

/*
* -------------------------------------------
*           Online validations
* -------------------------------------------
*/

export const moneyTransferSetConflictsAction =
    (payload: IWizardValidationError[]): TMoneyTransferSetConflictsAction =>
        createAction(MONEY_TRANSFER_SET_CONFLICTS, payload);

/*
* -------------------------------------------
*           HANDLE NEXT
* -------------------------------------------
*/

export const moneyTransferHandleNextAction = (): TMoneyTransferHandleNextAction =>
    createAction(MONEY_TRANSFER_HANDLE_NEXT);

/*
* -------------------------------------------
*           STORAGE
* -------------------------------------------
*/

export const moneyTransferStorageSaveStep =
    (payload?: IMoneyTransferStorageSaveStepPayload): TMoneyTransferStorageSaveStepAction =>
        createAction(MONEY_TRANSFER_STORAGE_SAVE_STEP, payload);

/*
* -------------------------------------------
*           CHECK RECIPIENTS
* -------------------------------------------
*/

export const moneyTransferCheckRecipientsStart =
    (): TMoneyTransferCheckRecipientsStartAction => createAction(MONEY_TRANSFER_CHECK_RECIPIENTS_START);
export const moneyTransferCheckRecipientsFinish =
    (): TMoneyTransferCheckRecipientsFinishAction => createAction(MONEY_TRANSFER_CHECK_RECIPIENTS_FINISH);


export type TMoneyTransferActions = TMoneyTransferSetSearchValueAction
    | TMoneyTransferClearSearchValueAction
    | TMoneyTransferLoadAccountListAction
    | TMoneyTransferLoadAccountListSuccessfulAction
    | TMoneyTransferLoadAccountListFailedAction
    | TMoneyTransferHandleNextAction
    | TMoneyTransferSetAccountAction
    | TMoneyTransferSetAmountAction
    | TMoneyTransferSetParamsAction
    | TMoneyTransferSetParamsErrorCodeAction
    | TMoneyTransferValidateStartAction
    | TMoneyTransferValidateSetStatusAction
    | TMoneyTransferSetConflictsAction
    | TMoneyTransferCheckRecipientsStartAction
    | TMoneyTransferCheckRecipientsFinishAction
    | ILogoutStartSuccessful
    ;
